import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import PageHeader from '../components/PageHeader';
import { supabase, getVehicleById } from '../lib/supabase';
import { formatCurrency } from '../lib/locale';

export default function AccessoriesPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const vehicleId = searchParams.get('vehicle');
  
  const [vehicle, setVehicle] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (!vehicleId) {
      setError('No vehicle specified');
      setLoading(false);
      return;
    }
    
    async function load() {
      try {
        const v = await getVehicleById(vehicleId);
        setVehicle(v);
        
        const { data, error: rErr } = await supabase
          .from('vehicle_accessory_recommendations')
          .select(`
            is_essential,
            fills_gap,
            accessories (*)
          `)
          .eq('vehicle_id', vehicleId)
          .order('is_essential', { ascending: false });
        
        if (rErr) throw rErr;
        setRecommendations(data || []);
        setLoading(false);
      } catch (e) {
        setError(e.message);
        setLoading(false);
      }
    }
    load();
  }, [vehicleId]);
  
  function getPrice(accessory) {
    switch (locale.country_code) {
      case 'IN': return accessory.price_inr;
      case 'US': return accessory.price_usd;
      case 'GB': return accessory.price_gbp;
      case 'FR':
      case 'DE': return accessory.price_eur;
      default: return accessory.price_usd;
    }
  }
  
  function getAffiliateUrl(accessory) {
    switch (locale.country_code) {
      case 'IN': return accessory.affiliate_url_amazon_in || accessory.affiliate_url_flipkart;
      case 'US': return accessory.affiliate_url_amazon_us;
      case 'GB': return accessory.affiliate_url_amazon_uk;
      default: return accessory.affiliate_url_other;
    }
  }
  
  if (loading) {
    return (
      <div className="page">
        <PageHeader title="Loading" backTo="/" />
        <div className="loading-state">Loading recommendations…</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="page">
        <PageHeader title="Accessories" backTo="/" />
        <div className="error-banner">{error}</div>
      </div>
    );
  }
  
  const totalAccuracyGain = recommendations
    .filter(r => r.is_essential)
    .reduce((s, r) => s + (r.accessories?.accuracy_improvement_pts || 0), 0);
  
  return (
    <div className="page">
      <PageHeader title="Improve accuracy" backTo="/" />
      
      {vehicle && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(32, 217, 166, 0.1) 0%, transparent 100%)',
          border: '0.5px solid rgba(32, 217, 166, 0.3)',
          borderRadius: 12,
          padding: 14,
          marginBottom: 14,
        }}>
          <div style={{
            fontSize: 10,
            color: 'var(--accent-primary)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 500,
            marginBottom: 4,
          }}>
            For your {vehicle.brand} {vehicle.model}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>
            These accessories close the sensor gaps in your vehicle. Each one tightens the prediction band.
          </div>
          {totalAccuracyGain > 0 && (
            <div style={{ fontSize: 11, color: 'var(--accent-primary)', marginTop: 8 }}>
              Total potential gain: ± {totalAccuracyGain.toFixed(1)} SoC points improvement
            </div>
          )}
        </div>
      )}
      
      {recommendations.length === 0 && (
        <div className="empty-state">
          {vehicle 
            ? `Your ${vehicle.brand} ${vehicle.model} has all the sensors we need. No accessories required.`
            : 'No recommendations yet.'}
        </div>
      )}
      
      {recommendations.map(rec => {
        const a = rec.accessories;
        if (!a) return null;
        const price = getPrice(a);
        const url = getAffiliateUrl(a);
        
        return (
          <div key={a.accessory_id} style={{
            background: 'var(--bg-card)',
            border: rec.is_essential ? '0.5px solid rgba(32, 217, 166, 0.3)' : '0.5px solid var(--border-subtle)',
            borderLeft: rec.is_essential ? '3px solid var(--accent-primary)' : '0.5px solid var(--border-subtle)',
            borderRadius: 12,
            padding: 14,
            marginBottom: 10,
          }}>
            {rec.is_essential && (
              <div style={{
                fontSize: 9,
                color: 'var(--accent-primary)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 500,
                marginBottom: 6,
              }}>
                Essential for accuracy
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 2 }}>
                  {a.name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                  {a.manufacturer} · {a.category.replace('_', ' ')}
                </div>
              </div>
              <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--accent-primary)' }}>
                {price ? formatCurrency(price, locale) : '—'}
              </div>
            </div>
            
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 10 }}>
              {a.description}
            </div>
            
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
              {a.measures_tpms && <Tag color="green">Tire pressure</Tag>}
              {a.measures_ambient_temp && <Tag color="amber">Ambient temp</Tag>}
              {a.measures_battery_temp && <Tag color="blue">Battery temp</Tag>}
              {a.measures_consumption && <Tag color="purple">Live consumption</Tag>}
            </div>
            
            {a.accuracy_improvement_pts > 0 && (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
                Tightens prediction by ± {a.accuracy_improvement_pts.toFixed(1)} SoC points
              </div>
            )}
            
            {url ? (
              <a href={url} target="_blank" rel="noopener noreferrer" style={{
                display: 'block',
                textAlign: 'center',
                background: rec.is_essential ? 'var(--accent-primary)' : 'transparent',
                color: rec.is_essential ? '#001a14' : 'var(--accent-primary)',
                border: rec.is_essential ? 'none' : '0.5px solid rgba(32, 217, 166, 0.5)',
                padding: '10px 12px',
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 500,
                textDecoration: 'none',
              }}>
                Buy on {locale.country_code === 'IN' ? 'Amazon / Flipkart' : 'partner store'} →
              </a>
            ) : (
              <div style={{ textAlign: 'center', padding: '10px 12px', fontSize: 11, color: 'var(--text-tertiary)' }}>
                Coming soon to your region
              </div>
            )}
          </div>
        );
      })}
      
      <div style={{
        marginTop: 20,
        padding: 12,
        background: 'var(--bg-secondary)',
        borderRadius: 10,
        fontSize: 11,
        color: 'var(--text-tertiary)',
        lineHeight: 1.5,
      }}>
        PluginAny earns a small commission when you buy through these links — at no extra cost to you. 
        We recommend accessories that genuinely close sensor gaps for your vehicle, not by paid placement.
      </div>
    </div>
  );
}

function Tag({ children, color }) {
  const colors = {
    green: { bg: 'rgba(32, 217, 166, 0.15)', fg: 'var(--accent-primary)' },
    amber: { bg: 'rgba(255, 184, 77, 0.15)', fg: 'var(--accent-warning)' },
    blue: { bg: 'rgba(108, 181, 255, 0.15)', fg: 'var(--accent-info)' },
    purple: { bg: 'rgba(180, 140, 255, 0.15)', fg: 'var(--accent-purple)' },
  };
  const c = colors[color] || colors.green;
  return (
    <span style={{
      fontSize: 10,
      padding: '2px 8px',
      background: c.bg,
      color: c.fg,
      borderRadius: 4,
    }}>
      {children}
    </span>
  );
}
